package com.devi3east.bashbuddy.ticketDetail.ui.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ConfirmationNumber
import androidx.compose.material.icons.filled.Person
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.devi3east.bashbuddy.dashboard.ui.components.dialogs.ConfirmDeleteDialog
import com.devi3east.bashbuddy.dashboard.ui.components.inputFields.NumberTextField
import com.devi3east.bashbuddy.purchase.domain.models.Allocation
import kotlin.math.max

@Composable
fun AllocationCard(
    ticketId: String,
    allocation: Allocation,
    chancesPerTicket: Int,
    assignedNumbers: List<String>,
    maxChanceNumber: Int,
    onSubmitAssign: (ticketId: String, assignments: List<Allocation>) -> Unit
) {
    val numDigits = maxChanceNumber.toString().length

    // Pad existing chance numbers and initialize selectedNumbers
    var selectedNumbers by remember(allocation.chanceNumbers, chancesPerTicket) {
        val base = if (allocation.chanceNumbers.isNotEmpty()) {
            allocation.chanceNumbers.map { it.padStart(numDigits, '0') } +
                    List(max(0, chancesPerTicket - allocation.chanceNumbers.size)) { "" }
        } else {
            List(chancesPerTicket) { "" }
        }
        mutableStateOf(base)
    }

    var duplicateNumbers by remember { mutableStateOf<List<String>>(emptyList()) }
    var rangeErrors by remember { mutableStateOf<List<String>>(emptyList()) }
    var showConfirmDialog by remember { mutableStateOf(false) }
    var attemptedOutOfRangeNumber by remember { mutableStateOf("") }

    val allAssigned = selectedNumbers.all { it.isNotBlank() } && duplicateNumbers.isEmpty()
    val numbers = allocation.chanceNumbers.map { it.padStart(numDigits, '0') }

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        elevation = CardDefaults.cardElevation(defaultElevation = 3.dp),
        colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface)
    ) {
        Column(modifier = Modifier.padding(16.dp)) {

            // --- Top Row: Contact info and number list ---
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Column(
                    modifier = Modifier.weight(1f),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(
                            Icons.Default.Person,
                            contentDescription = "User",
                            tint = MaterialTheme.colorScheme.primary
                        )
                        Spacer(Modifier.width(8.dp))
                        Text(
                            text = allocation.profile.name ?: "Unknown User",
                            style = MaterialTheme.typography.titleSmall.copy(fontWeight = FontWeight.SemiBold)
                        )
                    }

                    Text(
                        text = allocation.profile.email,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )

                    val phoneFormatted = allocation.profile.phone?.let { raw ->
                        raw.filter { it.isDigit() }.let { digits ->
                            if (digits.length == 10)
                                "(${digits.substring(0, 3)})-${digits.substring(3, 6)}-${digits.substring(6, 10)}"
                            else raw
                        }
                    } ?: "No phone"

                    Text(
                        text = phoneFormatted,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )

                    val address = allocation.profile.address
                    val addressFormatted = address?.let {
                        buildString {
                            if (it.line1.isNotBlank()) appendLine(it.line1)
                            if (it.city.isNotBlank() || it.state.isNotBlank())
                                append("${it.city}, ${it.state}")
                            if (it.zipCode.isNotBlank()) appendLine("\n${it.zipCode}")
                        }.trim()
                    } ?: "No address"

                    Text(
                        text = addressFormatted,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }

                if (numbers.isNotEmpty()) {
                    if (numbers.size <= 5) {
                        Column(
                            horizontalAlignment = Alignment.Start,
                            verticalArrangement = Arrangement.spacedBy(6.dp),
                            modifier = Modifier.padding(start = 12.dp)
                        ) {
                            numbers.forEach { number ->
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    Icon(
                                        Icons.Default.ConfirmationNumber,
                                        contentDescription = null,
                                        tint = MaterialTheme.colorScheme.primary,
                                        modifier = Modifier.size(28.dp)
                                    )
                                    Spacer(Modifier.width(8.dp))
                                    Text(
                                        text = number.ifBlank { "-" },
                                        style = MaterialTheme.typography.bodyLarge.copy(fontWeight = FontWeight.Bold)
                                    )
                                }
                            }
                        }
                    } else {
                        val leftCol = numbers.take(5)
                        val rightCol = numbers.drop(5)

                        Row(
                            horizontalArrangement = Arrangement.spacedBy(24.dp),
                            verticalAlignment = Alignment.Top,
                            modifier = Modifier.padding(start = 12.dp)
                        ) {
                            Column(
                                horizontalAlignment = Alignment.Start,
                                verticalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                leftCol.forEach { number ->
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(
                                            Icons.Default.ConfirmationNumber,
                                            contentDescription = null,
                                            tint = MaterialTheme.colorScheme.primary,
                                            modifier = Modifier.size(28.dp)
                                        )
                                        Spacer(Modifier.width(8.dp))
                                        Text(
                                            text = number.ifBlank { "-" },
                                            style = MaterialTheme.typography.bodyLarge.copy(
                                                fontWeight = FontWeight.Bold
                                            )
                                        )
                                    }
                                }
                            }

                            Column(
                                horizontalAlignment = Alignment.Start,
                                verticalArrangement = Arrangement.spacedBy(6.dp)
                            ) {
                                rightCol.forEach { number ->
                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                        Icon(
                                            Icons.Default.ConfirmationNumber,
                                            contentDescription = null,
                                            tint = MaterialTheme.colorScheme.primary,
                                            modifier = Modifier.size(28.dp)
                                        )
                                        Spacer(Modifier.width(8.dp))
                                        Text(
                                            text = number.ifBlank { "-" },
                                            style = MaterialTheme.typography.bodyLarge.copy(
                                                fontWeight = FontWeight.Bold
                                            )
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // --- Number TextField grid for empty numbers ---
            if (numbers.isEmpty()) {
                Spacer(Modifier.height(12.dp))

                val columns = 2
                val rows = ((chancesPerTicket + columns - 1) / columns).coerceAtMost(5)

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    for (rowIndex in 0 until rows) {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            for (colIndex in 0 until columns) {
                                val index = rowIndex * columns + colIndex
                                if (index < chancesPerTicket) {
                                    val currentValue = selectedNumbers.getOrElse(index) { "" }

                                    NumberTextField(
                                        label = "${index + 1}",
                                        value = currentValue,
                                        onValueChange = { newValue ->
                                            // Allow digits only, but DO NOT pad yet
                                            val cleaned = newValue.filter { it.isDigit() }

                                            // Limit to numDigits (prevent user typing more than possible)
                                            if (cleaned.length <= numDigits) {
                                                val newSelected = selectedNumbers.toMutableList().apply {
                                                    if (index < size) this[index] = cleaned
                                                    else {
                                                        while (size <= index) add("")
                                                        this[index] = cleaned
                                                    }
                                                }

                                                // duplicate check using padded values
                                                val normalizedAssigned = assignedNumbers.map { it.padStart(numDigits, '0') }
                                                val normalizedNewSelected = newSelected.map { it.padStart(numDigits, '0') }

                                                val conflicts = normalizedNewSelected
                                                    .filter { it.isNotBlank() }
                                                    .groupingBy { it }
                                                    .eachCount()
                                                    .filter { (value, count) -> normalizedAssigned.contains(value) || count > 1 }
                                                    .keys
                                                    .toList()

                                                // range errors using numeric comparison
                                                val outOfRange = cleaned
                                                    .takeIf { it.isNotBlank() }
                                                    ?.toInt()
                                                    ?.let { if (it > maxChanceNumber) listOf(it.toString()) else emptyList() }
                                                    ?: emptyList()

                                                duplicateNumbers = conflicts.distinct()
                                                rangeErrors = outOfRange
                                                selectedNumbers = newSelected
                                            }
                                        },
                                        modifier = Modifier.weight(1f)
                                    )
                                } else {
                                    Spacer(modifier = Modifier.weight(1f))
                                }
                            }
                        }
                    }

                    Spacer(Modifier.height(12.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween
                    ) {
                        if (duplicateNumbers.isNotEmpty()) {
                            val errorText = when (duplicateNumbers.size) {
                                1 -> "Number ${duplicateNumbers.first()} is already taken."
                                2 -> "Numbers ${duplicateNumbers.joinToString(" and ")} are already taken."
                                else -> {
                                    val allButLast = duplicateNumbers.dropLast(1).joinToString(", ")
                                    val last = duplicateNumbers.last()
                                    "Numbers $allButLast, and $last are already taken."
                                }
                            }

                            Text(
                                text = errorText,
                                color = MaterialTheme.colorScheme.error,
                                style = MaterialTheme.typography.bodySmall,
                                modifier = Modifier.weight(1f)
                            )
                        } else {
                            Spacer(Modifier.weight(1f))
                        }

                        Button(
                            onClick = {
                                val normalized = selectedNumbers.map {
                                    it.padStart(numDigits, '0')
                                }

                                if (rangeErrors.isNotEmpty()) {
                                    attemptedOutOfRangeNumber = rangeErrors.first()
                                    showConfirmDialog = true
                                } else {
                                    val updatedAllocation = allocation.copy(chanceNumbers = normalized)
                                    onSubmitAssign(ticketId, listOf(updatedAllocation))
                                }
                            },
                            enabled = allAssigned,
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Text("Submit")
                        }
                    }
                }
            }
        }
    }

    // --- Confirm Dialog for Out-of-Range Numbers ---
    if (showConfirmDialog) {
        val minFormatted = "0".repeat(numDigits)
        val maxFormatted = maxChanceNumber.toString().padStart(numDigits, '0')

        val messageText = if (rangeErrors.size == 1) {
            "Based on the ticket information, each ticket should have ($chancesPerTicket) numbers per ticket, " +
                    "with each number between $minFormatted and $maxFormatted. " +
                    "This number (${rangeErrors.first()}) is out of that range. Are you sure you want to submit it? Once submitted, " +
                    "you will not be able to change it without contacting support."
        } else {
            val numbersList = rangeErrors.joinToString(", ")
            "Based on the ticket information, each ticket should have ($chancesPerTicket) numbers per ticket, " +
                    "with each number between $minFormatted and $maxFormatted. " +
                    "These numbers ($numbersList) are out of that range. Are you sure you want to submit them? Once submitted, " +
                    "you will not be able to change them without contacting support."
        }

        ConfirmDeleteDialog(
            title = "Out-of-Range Number${if (rangeErrors.size > 1) "s" else ""}",
            message = messageText,
            onConfirm = {
                val updatedAllocation = allocation.copy(chanceNumbers = selectedNumbers)
                onSubmitAssign(ticketId, listOf(updatedAllocation))
                showConfirmDialog = false
            },
            onDismiss = { showConfirmDialog = false },
            confirmButtonText = "Yes",
            dismissButtonText = "No"
        )
    }
}